import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { deserializeDocumentBuyerModel, deserializeDocumentClientModel, serializeDocumentBuyerModel, serializeDocumentClientModel, serializeDocumentExchangeRateModel, serializeDocumentPaymentListItemModel } from '@skylab-shared/business';
import { HttpService, ObservableCollection, QueryBus as QueryBusBase, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { CalculationMethod, DocumentPaymentListItemModel, ExchangeRateModel, PurchaseBankAccountModel, PurchaseGetDocumentDTO, PurchaseInvoiceAdjustmentDocumentModel, PurchaseInvoiceDocumentItemModel, PurchaseUpdateDocumentDTO, RelatedEntity, VatTableItemModel } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { PurchaseInvoiceAdjustmentDocumentBO } from './purchase-invoice-adjustment-document.bo';

@Repository({
    boType: PurchaseInvoiceAdjustmentDocumentBO,
    apiController: TaggedUrl.join('purchase', [Tags.Api]),
    dto: {
        get: PurchaseGetDocumentDTO,
        update: PurchaseUpdateDocumentDTO
    }
})
@Injectable({ providedIn: 'root'})
export class PurchaseInvoiceAdjustmentDocumentRepository extends RepositoryBase<PurchaseInvoiceAdjustmentDocumentBO, PurchaseInvoiceAdjustmentDocumentModel, PurchaseGetDocumentDTO, PurchaseUpdateDocumentDTO> {
    public constructor(
        http: Http,
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus,
        private readonly productRepo: ProductRepository
    ) {
        super(http as HttpService);
    }

    protected createBO(): PurchaseInvoiceAdjustmentDocumentBO {
        return new PurchaseInvoiceAdjustmentDocumentBO(this.weaver as WeaverBase, this.queryBus as any as QueryBusBase, this.productRepo);
    }

    protected async fromPlainObjectAsync(item: PurchaseGetDocumentDTO): Promise<PurchaseInvoiceAdjustmentDocumentModel> {
        return new PurchaseInvoiceAdjustmentDocumentModel(pickBy<Partial<PurchaseInvoiceAdjustmentDocumentModel>>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,
            favourite: item.favourite,

            automaticVatCalculation: item.automaticVatCalculation,
            issueGoodsReceiptAdjustment: item.issueGoodsReceiptAdjustment,
            inventoryEffect: item.inventoryEffect,

            adjustmentReason: item.adjustmentReason,

            ddkState: item.ddkState,
            eppState: item.eppState,

            relatedInventoryDocuments: item.relatedInventoryDocuments?.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            includesInventoryControl: item.includesInventoryControl,

            adjustedDocumentVersion: item.adjustedDocumentVersion,
            adjustedDocumentId: item.adjustedDocumentId,

            calculationMethod: item.calculationMethod,
            costCalculationMethod: item.costCalculationMethod,
            cashBasis: item.cashBasis,
            seller: deserializeDocumentClientModel(item.seller),
            supplier: deserializeDocumentBuyerModel(item.supplier),
            bankAccount: item.sellerBankAccount
                ? new PurchaseBankAccountModel(pickBy<PurchaseBankAccountModel>({
                    bankName: item.sellerBankAccount.bankName,
                    number: item.sellerBankAccount.number,
                    status: item.sellerBankAccount.whiteListStatus,
                    statusDate: item.sellerBankAccount.whiteListLastDateCheck,
                    statusId: item.sellerBankAccount.whiteListCheckingId
                }, val => val !== void 0))
                : null,
            documentNumber: item.documentNumber,
            exchangeRate: item.exchangeRate ? new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                units: item.exchangeRate.units,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
            }, val => val !== void 0)) : null,
            expenseName: item.expenseName,
            externalDocumentNumber: item.externalDocumentNumber,
            kind: item.kind,
            note: item.note,
            numberingId: item.numberingId,
            splitPayment: item.splitPayment,
            jpkVatGroups: item.jpkVatDocumentGroups,

            grossValue: item.totalBaseCurrencyGrossValue,
            currencyGrossValue: item.exchangeRate ? item.totalGrossValue : null,

            netValue: item.totalBaseCurrencyNetValue,

            fixedAsset: item.fixedAsset,
            issuedByVatPayer: item.issuedByVatPayer,
            transactionLocation: item.transactionLocation,

            deliveryDate: item.deliveryDate,
            issueDate: item.issueDate,
            receiptDate: item.receiptDate,
            vatDeductionDate: item.vatDeductionDate,

            dueDate: item.dueDate ? new Date(item.dueDate) : null,

            recipient: item.recipient,
            issuer: item.issuer,

            communityTransactionId: item.communityTransactionId,


            currencyId: item.currencyId,
            paymentMethodId: item.paymentMethodId,
            items: new ObservableCollection(item.items.map(documentItem => {
                return new PurchaseInvoiceDocumentItemModel(pickBy<Partial<PurchaseInvoiceDocumentItemModel>>({
                    ordinalNumber: documentItem.no,
                    discountKind: documentItem.discountKind,
                    discountValue: documentItem.discountValue,
                    jpkVatGroups: documentItem.jpkVatProductGroup ? [documentItem.jpkVatProductGroup] : [],
                    note: documentItem.note,
                    price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                    price_grossBeforeDiscount: documentItem.unitGrossPrice,
                    price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                    id: documentItem.id,
                    price_netBeforeDiscount: documentItem.unitNetPrice,
                    adjustedItemId: documentItem.adjustedItemId,
                    productId: documentItem.productId,
                    includesInventoryControl: documentItem.includesInventoryControl,
                    relatedInventoryItemsIds: documentItem.relatedInventoryItemsIds || [],
                    productName: documentItem.productName,
                    pkwiu: documentItem.pkwiu,
                    cnCode: documentItem.cnCode,
                    symbol: documentItem.symbol,
                    barCode: documentItem.barCode,
                    quantity: documentItem.quantity,
                    unitId: documentItem.unitId,
                    value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                    value_grossBeforeDiscount: documentItem.grossValue,
                    value_netAfterDiscount: documentItem.netValueAfterDiscount,
                    value_netBeforeDiscount: documentItem.netValue,
                    value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                    value_vatBeforeDiscount: documentItem.vatValue,
                    vatRateId: documentItem.vatRateId,
                    inventoryTotalCost: documentItem.inventoryTotalCost,
                    inventoryUnitCost: documentItem.inventoryUnitCost
                }, val => val !== void 0));
            })),
            payments: new ObservableCollection(item.payments.map(item => {
                return new DocumentPaymentListItemModel(pickBy<Partial<DocumentPaymentListItemModel>>({
                    id: item.id,
                    date: item.date,
                    value: item.amount,
                    onDocument: item.onDocument
                }, val => val !== void 0));
            })),
            vatTableItems: new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.gross,
                    netValue: item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            }))
        }, val => val !== void 0));
    }

    protected async preparePlainObjectAsync(model: PurchaseInvoiceAdjustmentDocumentModel): Promise<PurchaseUpdateDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            favourite: model.favourite,

            relatedInventoryDocuments: model.relatedInventoryDocuments?.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            inventoryEffect: model.inventoryEffect,

            recipient: model.recipient,
            issuer: model.issuer,

            automaticVatCalculation: model.automaticVatCalculation,
            issueGoodsReceiptAdjustment: model.issueGoodsReceiptAdjustment,

            adjustedDocumentVersion: model.adjustedDocumentVersion,
            adjustedDocumentId: model.adjustedDocumentId,
            adjustmentReason: model.adjustmentReason,

            calculationMethod: model.calculationMethod,
            payments: model.payments.map(serializeDocumentPaymentListItemModel),
            costCalculationMethod: model.costCalculationMethod,
            cashBasis: model.cashBasis,
            seller: serializeDocumentClientModel(model.seller),
            supplier: serializeDocumentBuyerModel(model.supplier),
            sellerBankAccount: model.bankAccount
                ? {
                    bankName: model.bankAccount.bankName,
                    number: model.bankAccount.number,
                    whiteListStatus: model.bankAccount.status,
                    whiteListLastDateCheck: model.bankAccount.statusDate,
                    whiteListCheckingId: model.bankAccount.statusId
                }
                : null,
            documentNumber: model.documentNumber,
            expenseName: model.expenseName,
            externalDocumentNumber: model.externalDocumentNumber,
            kind: model.kind,
            note: model.note,
            numberingId: model.numberingId,
            splitPayment: model.splitPayment,
            jpkVatDocumentGroups: model.jpkVatGroups,

            fixedAsset: model.fixedAsset,

            issueDate: model.issueDate ? getStartOfDate(model.issueDate) : null,
            receiptDate: model.receiptDate ? getStartOfDate(model.receiptDate) : null,
            deliveryDate: getStartOfDate(model.deliveryDate),
            vatDeductionDate: getStartOfDate(model.vatDeductionDate),
            dueDate: model.dueDate ? getStartOfDate(model.dueDate) : null,

            transactionLocation: model.transactionLocation,

            communityTransactionId: model.communityTransactionId,
            items: model.items.map(elm => ({
                discountValue: elm.discountValue,
                discountKind: elm.discountKind,
                no: elm.ordinalNumber,
                note: elm.note,
                productId: elm.productId,
                productName: elm.productName,
                pkwiu: elm.pkwiu,
                symbol: elm.symbol,
                barCode: elm.barCode,
                adjustedItemId: elm.adjustedItemId,
                cnCode: elm.cnCode,
                jpkVatProductGroup: elm.jpkVatGroup,
                quantity: elm.quantity,
                includesInventoryControl: elm.includesInventoryControl,
                relatedInventoryItemsIds: elm.relatedInventoryItemsIds || [],
                unitId: elm.unitId,
                price: model.calculationMethod === CalculationMethod.Gross ? elm.price_grossBeforeDiscount : elm.price_netBeforeDiscount,
                vatRateId: elm.vatRateId
            })),
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null,
            currencyId: model.currencyId,
            paymentMethodId: model.paymentMethodId,
            vatTable: {
                items: model.vatTableItems.map(item => {
                    return {
                        gross: item.grossValue,
                        net: item.netValue,
                        vat: item.vatValue,
                        vatRateId: item.vatRateId
                    };
                })
            },
        };
    }
}
